import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, AlertController, Platform, IonSearchbar, PopoverController } from '@ionic/angular/lazy';
import { APIService } from 'src/app/services/api.service';
import { Capacitor } from '@capacitor/core';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import { CartService } from 'src/app/services/cart.service';
import { ObserverService } from 'src/app/services/observer.service';
import { ShippingAddressAddPage } from 'src/app/modals/shipping-address-add/shipping-address-add.page';
import { AddressBookShippingAddressOptionsComponent } from 'src/app/popovers/address-book-shipping-address-options/address-book-shipping-address-options.component';
import { PermissionService } from 'src/app/services/permission.service';

@Component({
    selector: 'app-address-picker',
    templateUrl: './address-picker.page.html',
    styleUrls: ['./address-picker.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddressPickerPage implements OnInit {

    @ViewChild('search_bar_input', { static: false }) search_bar_input: IonSearchbar;

    online = true;
    params = {};
    person = null;
    organisation = null;
    addresses = null;
    visible_addresses = null;
    address = null;
    modal_css_class = '';
    loading = false;
    cart = null;
    query = null;

    // modal flags
    add_shipping_address_modal_open = false;

    constructor(
        private router: Router,
        private apiService: APIService,
        private authService: AuthService,
        public modalController: ModalController,
        public alertController: AlertController,
        private platform: Platform,
        private cartService: CartService,
        private observerService: ObserverService,
        public popoverController: PopoverController,
        public permissionService: PermissionService
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.modal_css_class = 'fullscreen';
        }

        this.loadData().then(() => {
            this.showInitialVisibleAddresses().then(() => {}).catch(() => { });
        })
        .catch(() => { });

        this.observerService.subscribe('organisation_shipping_address_added|address_picker_modal', shipping_address => {
            
            if(shipping_address) {

                if(this.organisation && this.organisation.id == shipping_address.organisation.id) {

                    let address = {};

                    address['weight'] = 400;
                    address['type'] = "Shipping address";
                    address['entity_type'] = 'organisation';
                    address['shipping_address_id'] = shipping_address.id;

                    if(shipping_address.first_name) {
                        address['first_name'] = shipping_address.first_name;
                    }
                    if(shipping_address.last_name) {
                        address['last_name'] = shipping_address.last_name;
                    }
                    if(shipping_address.organisation_title) {
                        address["organisation_title"] = shipping_address.organisation_title;
                    }
                    if(shipping_address.premises) {
                        address["premises"] = shipping_address.premises;
                    }
                    if(shipping_address.line_1) {
                        address["line_1"] = shipping_address.line_1;
                    }
                    if(shipping_address.line_2) {
                        address["line_2"] = shipping_address.line_2;
                    }
                    if(shipping_address.subdivision) {
                        address["subdivision"] = shipping_address.subdivision;
                    }
                    if(shipping_address.locality) {
                        address["locality"] = shipping_address.locality;
                    }
                    if(shipping_address.postal_code) {
                        address["postal_code"] = shipping_address.postal_code;
                    }
                    if(shipping_address.country) {
                        address["country_id"] = shipping_address.country.id;
                        address['country_title'] = shipping_address.country.title;
                    }
                    if (shipping_address.mobile_phone_number) {
                        address["mobile_phone_number"] = shipping_address.mobile_phone_number;
                    }
                    if (shipping_address.what3words_address) {
                        address["what3words_address"] = shipping_address.what3words_address;
                    }
                    if (shipping_address.courier_instructions) {
                        address["courier_instructions"] = shipping_address.courier_instructions;
                    }

                    this.addresses.push(address)

                    this.addresses.sort((a, b) => {
                        if (a.weight > b.weight) { return -1; }
                        if (a.weight < b.weight) { return 1; }
                        return 0;
                    });

                    this.searchAddresses();

                }
            }
        });

        this.observerService.subscribe('organisation_shipping_address_updated|address_picker_modal', shipping_address => {

            if(this.addresses) {

                for(let i = 0; i < this.addresses.length; i++) {

                    if(this.addresses[i].shipping_address_id && this.addresses[i].shipping_address_id == shipping_address.id) {

                        delete this.addresses[i]['first_name'];
                        delete this.addresses[i]['last_name'];
                        delete this.addresses[i]['premises'];
                        delete this.addresses[i]['line_1'];
                        delete this.addresses[i]['line_2'];
                        delete this.addresses[i]['subdivision'];
                        delete this.addresses[i]['locality'];
                        delete this.addresses[i]['postal_code'];
                        delete this.addresses[i]['country_id'];
                        delete this.addresses[i]['country_title'];
                        delete this.addresses[i]['mobile_phone_number'];
                        delete this.addresses[i]['what3words_address'];
                        delete this.addresses[i]['courier_instructions'];

                        if(shipping_address.first_name) {
                            this.addresses[i]['first_name'] = shipping_address.first_name;
                        }
                        if(shipping_address.last_name) {
                            this.addresses[i]['last_name'] = shipping_address.last_name;
                        }
                        if(shipping_address.premises) {
                            this.addresses[i]["premises"] = shipping_address.premises;
                        }
                        if(shipping_address.line_1) {
                            this.addresses[i]["line_1"] = shipping_address.line_1;
                        }
                        if(shipping_address.line_2) {
                            this.addresses[i]["line_2"] = shipping_address.line_2;
                        }
                        if(shipping_address.subdivision) {
                            this.addresses[i]["subdivision"] = shipping_address.subdivision;
                        }
                        if(shipping_address.locality) {
                            this.addresses[i]["locality"] = shipping_address.locality;
                        }
                        if(shipping_address.postal_code) { 
                            this.addresses[i]["postal_code"] = shipping_address.postal_code;
                        }
                        if(shipping_address.country) {
                            this.addresses[i]["country_id"] = shipping_address.country.id;
                            this.addresses[i]['country_title'] = shipping_address.country.title;
                        }
                        if(shipping_address.mobile_phone_number) {
                            this.addresses[i]["mobile_phone_number"] = shipping_address.mobile_phone_number;
                        }
                        if(shipping_address.what3words_address) {
                            this.addresses[i]["what3words_address"] = shipping_address.what3words_address;
                        }
                        if(shipping_address.courier_instructions) {
                            this.addresses[i]["courier_instructions"] = shipping_address.courier_instructions;
                        }

                        this.searchAddresses();

                        break;
                    }

                }
            }
        });

        this.observerService.subscribe('organisation_shipping_address_deleted|address_picker_modal', shipping_address_id => {

            if(this.visible_addresses) {
                for(let i = 0; i < this.visible_addresses.length; i++) {
                    if(this.visible_addresses[i].shipping_address_id && this.visible_addresses[i].shipping_address_id == shipping_address_id) {
                        this.visible_addresses.splice(i, 1);
                        break;
                    }
                }
            }

            if(this.addresses) {
                for(let i = 0; i < this.addresses.length; i++) {
                    if(this.addresses[i].shipping_address_id && this.addresses[i].shipping_address_id == shipping_address_id) {
                        this.addresses.splice(i, 1);
                        break;
                    }
                }
            }

        });

        this.observerService.subscribe('person_shipping_address_added|address_picker_modal', shipping_address => {

            if(shipping_address) {

                if(!this.organisation && this.person && this.person.id == shipping_address.person.id) {

                    let address = {};

                    address['weight'] = 300;
                    address['type'] = "Shipping address";
                    address['entity_type'] = 'person';
                    address['shipping_address_id'] = shipping_address.id;

                    if(shipping_address.first_name) {
                        address['first_name'] = shipping_address.first_name;
                    }
                    if(shipping_address.last_name) {
                        address['last_name'] = shipping_address.last_name;
                    }
                    if(shipping_address.premises) {
                        address["premises"] = shipping_address.premises;
                    }
                    if(shipping_address.line_1) {
                        address["line_1"] = shipping_address.line_1;
                    }
                    if(shipping_address.line_2) {
                        address["line_2"] = shipping_address.line_2;
                    }
                    if(shipping_address.subdivision) {
                        address["subdivision"] = shipping_address.subdivision;
                    }
                    if(shipping_address.locality) {
                        address["locality"] = shipping_address.locality;
                    }
                    if(shipping_address.postal_code) { 
                        address["postal_code"] = shipping_address.postal_code;
                    }
                    if(shipping_address.country) {
                        address["country_id"] = shipping_address.country.id;
                        address['country_title'] = shipping_address.country.title;
                    }
                    if (shipping_address.mobile_phone_number) {
                        address["mobile_phone_number"] = shipping_address.mobile_phone_number;
                    }
                    if (shipping_address.what3words_address) {
                        address["what3words_address"] = shipping_address.what3words_address;
                    }
                    if (shipping_address.courier_instructions) {
                        address["courier_instructions"] = shipping_address.courier_instructions;
                    }

                    this.addresses.push(address)

                    this.addresses.sort((a, b) => {
                        if (a.weight > b.weight) { return -1; }
                        if (a.weight < b.weight) { return 1; }
                        return 0;
                    })

                    this.searchAddresses();

                }
            }
        });

        this.observerService.subscribe('person_shipping_address_updated|address_picker_modal', shipping_address => {

            if(this.addresses) {

                for(let i = 0; i < this.addresses.length; i++) {

                    if(this.addresses[i].shipping_address_id && this.addresses[i].shipping_address_id == shipping_address.id) {

                        delete this.addresses[i]['first_name'];
                        delete this.addresses[i]['last_name'];
                        delete this.addresses[i]['premises'];
                        delete this.addresses[i]['line_1'];
                        delete this.addresses[i]['line_2'];
                        delete this.addresses[i]['subdivision'];
                        delete this.addresses[i]['locality'];
                        delete this.addresses[i]['postal_code'];
                        delete this.addresses[i]['country_id'];
                        delete this.addresses[i]['country_title'];
                        delete this.addresses[i]['mobile_phone_number'];
                        delete this.addresses[i]['what3words_address'];
                        delete this.addresses[i]['courier_instructions'];

                        if(shipping_address.first_name) {
                            this.addresses[i]['first_name'] = shipping_address.first_name;
                        }
                        if(shipping_address.last_name) {
                            this.addresses[i]['last_name'] = shipping_address.last_name;
                        }
                        if(shipping_address.premises) {
                            this.addresses[i]["premises"] = shipping_address.premises;
                        }
                        if(shipping_address.line_1) {
                            this.addresses[i]["line_1"] = shipping_address.line_1;
                        }
                        if(shipping_address.line_2) {
                            this.addresses[i]["line_2"] = shipping_address.line_2;
                        }
                        if(shipping_address.subdivision) {
                            this.addresses[i]["subdivision"] = shipping_address.subdivision;
                        }
                        if(shipping_address.locality) {
                            this.addresses[i]["locality"] = shipping_address.locality;
                        }
                        if(shipping_address.postal_code) { 
                            this.addresses[i]["postal_code"] = shipping_address.postal_code;
                        }
                        if(shipping_address.country) {
                            this.addresses[i]["country_id"] = shipping_address.country.id;
                            this.addresses[i]['country_title'] = shipping_address.country.title;
                        }
                        if(shipping_address.mobile_phone_number) {
                            this.addresses[i]["mobile_phone_number"] = shipping_address.mobile_phone_number;
                        }
                        if(shipping_address.what3words_address) {
                            this.addresses[i]["what3words_address"] = shipping_address.what3words_address;
                        }
                        if(shipping_address.courier_instructions) {
                            this.addresses[i]["courier_instructions"] = shipping_address.courier_instructions;
                        }

                        this.searchAddresses();

                        break;
                    }

                }
            }
        });

        this.observerService.subscribe('person_shipping_address_deleted|address_picker_modal', shipping_address_id => {

            if(this.visible_addresses) {
                for(let i = 0; i < this.visible_addresses.length; i++) {
                    if(this.visible_addresses[i].shipping_address_id && this.visible_addresses[i].shipping_address_id == shipping_address_id) {
                        this.visible_addresses.splice(i, 1);
                        break;
                    }
                }
            }

            if(this.addresses) {
                for(let i = 0; i < this.addresses.length; i++) {
                    if(this.addresses[i].shipping_address_id && this.addresses[i].shipping_address_id == shipping_address_id) {
                        this.addresses.splice(i, 1);
                        break;
                    }
                }
            }

        });

        this.observerService.subscribe('network_status_changed|address_picker_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => {} ).catch(() => { });
                }
                else {
                    this.presentAlert("Offline", "You must be online to choose an address, please check your network connection and try again.");
                }
            }
        });

    }

    ngOnDestroy() {
        this.observerService.destroy("organisation_shipping_address_added|address_picker_modal");
        this.observerService.destroy("organisation_shipping_address_updated|address_picker_modal");
        this.observerService.destroy("person_shipping_address_deleted|address_picker_modal");
        this.observerService.destroy("person_shipping_address_added|address_picker_modal");
        this.observerService.destroy("person_shipping_address_updated|address_picker_modal");
        this.observerService.destroy("person_shipping_address_deleted|address_picker_modal");
        this.observerService.destroy("network_status_changed|address_picker_modal");
    }

    loadData() {

        return new Promise<void>((resolve, reject) => {

            this.addresses = [];
            this.visible_addresses = [];
            this.loading = true;

            this.cartService.get().then(async (cart) => {

                if(cart) {

                    this.cart = cart;

                    if(cart['person']) {

                        await this.loadPerson(cart['person'].id).then(async (person) => {

                            if(person) {

                                this.person = person;

                                let addresses = [];

                                if(cart['organisation']) {

                                    await this.loadOrganisation(cart['organisation'].id).then(async (organisation) => {
            
                                        if(organisation) {
            
                                            this.organisation = organisation;

                                            if(this.organisation.mailing_address) {

                                                let address = {}

                                                address['weight'] = 300;
                                                address['type'] = "Mailing address";
                                                address['entity_type'] = 'organisation';

                                                if (this.person.first_name) {
                                                    address['first_name'] = this.person.first_name;
                                                }
                                                if (this.person.last_name) {
                                                    address['last_name'] = this.person.last_name;
                                                }
                                                if (this.organisation.title) {
                                                    address["organisation_title"] = this.organisation.title;
                                                }
                                                if (this.organisation.mailing_address.premises) {
                                                    address["premises"] = this.organisation.mailing_address.premises;
                                                }
                                                if (this.organisation.mailing_address.line_1) {
                                                    address["line_1"] = this.organisation.mailing_address.line_1;
                                                }
                                                if (this.organisation.mailing_address.line_2) {
                                                    address["line_2"] = this.organisation.mailing_address.line_2;
                                                }
                                                if (this.organisation.mailing_address.subdivision) {
                                                    address["subdivision"] = this.organisation.mailing_address.subdivision;
                                                }
                                                if (this.organisation.mailing_address.locality) {
                                                    address["locality"] = this.organisation.mailing_address.locality;
                                                }
                                                if (this.organisation.mailing_address.postal_code) {
                                                    address["postal_code"] = this.organisation.mailing_address.postal_code;
                                                }
                                                if (this.organisation.mailing_address.country) {
                                                    address["country_id"] = this.organisation.mailing_address.country.id;
                                                    address['country_title'] = this.organisation.mailing_address.country.title;
                                                }
                                                if (this.organisation.mailing_address.mobile_phone_number) {
                                                    address["mobile_phone_number"] = this.organisation.mailing_address.mobile_phone_number;
                                                }
                                                if (this.organisation.mailing_address.what3words_address) {
                                                    address["what3words_address"] = this.organisation.mailing_address.what3words_address;
                                                }
                                                if (this.organisation.mailing_address.courier_instructions) {
                                                    address["courier_instructions"] = this.organisation.mailing_address.courier_instructions;
                                                }

                                                addresses.push(address)

                                            }

                                            if(this.organisation.physical_address) {

                                                let address = {}

                                                address['weight'] = 100;
                                                address['type'] = "Physical address";
                                                address['entity_type'] = 'organisation';

                                                if (this.person.first_name) {
                                                    address['first_name'] = this.person.first_name;
                                                }
                                                if (this.person.last_name) {
                                                    address['last_name'] = this.person.last_name;
                                                }
                                                if (this.organisation.title) {
                                                    address["organisation_title"] = this.organisation.title;
                                                }
                                                if (this.organisation.physical_address.premises) {
                                                    address["premises"] = this.organisation.physical_address.premises;
                                                }
                                                if (this.organisation.physical_address.line_1) {
                                                    address["line_1"] = this.organisation.physical_address.line_1;
                                                }
                                                if (this.organisation.physical_address.line_2) {
                                                    address["line_2"] = this.organisation.physical_address.line_2;
                                                }
                                                if (this.organisation.physical_address.subdivision) {
                                                    address["subdivision"] = this.organisation.physical_address.subdivision;
                                                }
                                                if (this.organisation.physical_address.locality) {
                                                    address["locality"] = this.organisation.physical_address.locality;
                                                }
                                                if (this.organisation.physical_address.postal_code) {
                                                    address["postal_code"] = this.organisation.physical_address.postal_code;
                                                }
                                                if (this.organisation.physical_address.country) {
                                                    address["country_id"] = this.organisation.physical_address.country.id;
                                                    address['country_title'] = this.organisation.physical_address.country.title;
                                                }
                                                if (this.organisation.physical_address.mobile_phone_number) {
                                                    address["mobile_phone_number"] = this.organisation.physical_address.mobile_phone_number;
                                                }
                                                if (this.organisation.physical_address.what3words_address) {
                                                    address["what3words_address"] = this.organisation.physical_address.what3words_address;
                                                }
                                                if (this.organisation.physical_address.courier_instructions) {
                                                    address["courier_instructions"] = this.organisation.physical_address.courier_instructions;
                                                }

                                                addresses.push(address)

                                            }

                                            await this.loadOrganisationShippingAddresses(this.organisation.id).then((shipping_addresses: any) => {
            
                                                if(shipping_addresses) {
            
                                                    for(let i = 0; i < shipping_addresses.length; i++) {

                                                        let address = {}

                                                        address['weight'] = 400;
                                                        address['type'] = "Shipping address";
                                                        address['entity_type'] = 'organisation';
                                                        address['shipping_address_id'] = shipping_addresses[i].id;
            
                                                        if (shipping_addresses[i].first_name) {
                                                            address['first_name'] = shipping_addresses[i].first_name;
                                                        }
                                                        if (shipping_addresses[i].last_name) {
                                                            address['last_name'] = shipping_addresses[i].last_name;
                                                        }
                                                        if (shipping_addresses[i].organisation_title) {
                                                            address["organisation_title"] = shipping_addresses[i].organisation_title;
                                                        }
                                                        if (shipping_addresses[i].premises) {
                                                            address["premises"] = shipping_addresses[i].premises;
                                                        }
                                                        if (shipping_addresses[i].line_1) {
                                                            address["line_1"] = shipping_addresses[i].line_1;
                                                        }
                                                        if (shipping_addresses[i].line_2) {
                                                            address["line_2"] = shipping_addresses[i].line_2;
                                                        }
                                                        if (shipping_addresses[i].subdivision) {
                                                            address["subdivision"] = shipping_addresses[i].subdivision;
                                                        }
                                                        if (shipping_addresses[i].locality) {
                                                            address["locality"] = shipping_addresses[i].locality;
                                                        }
                                                        if (shipping_addresses[i].postal_code) {
                                                            address["postal_code"] = shipping_addresses[i].postal_code;
                                                        }
                                                        if (shipping_addresses[i].country) {
                                                            address["country_id"] = shipping_addresses[i].country.id;
                                                            address['country_title'] = shipping_addresses[i].country.title;
                                                        }
                                                        if (shipping_addresses[i].mobile_phone_number) {
                                                            address["mobile_phone_number"] = shipping_addresses[i].mobile_phone_number;
                                                        }
                                                        if (shipping_addresses[i].what3words_address) {
                                                            address["what3words_address"] = shipping_addresses[i].what3words_address;
                                                        }
                                                        if (shipping_addresses[i].courier_instructions) {
                                                            address["courier_instructions"] = shipping_addresses[i].courier_instructions;
                                                        }
            
                                                        addresses.push(address)
            
                                                    }
                                                }
                                            })
                                        }
                                    })
                                }

                                if(addresses.length == 0) {

                                    if(this.person.mailing_address) {

                                        let address = {}

                                        address['weight'] = 200;
                                        address['type'] = "Mailing address";
                                        address['entity_type'] = 'person';

                                        if (this.person.first_name) {
                                            address['first_name'] = this.person.first_name;
                                        }
                                        if (this.person.last_name) {
                                            address['last_name'] = this.person.last_name;
                                        }
                                        if (this.person.title) {
                                            address["person_title"] = this.person.title;
                                        }
                                        if (this.person.mailing_address.premises) {
                                            address["premises"] = this.person.mailing_address.premises;
                                        }
                                        if (this.person.mailing_address.line_1) {
                                            address["line_1"] = this.person.mailing_address.line_1;
                                        }
                                        if (this.person.mailing_address.line_2) {
                                            address["line_2"] = this.person.mailing_address.line_2;
                                        }
                                        if (this.person.mailing_address.subdivision) {
                                            address["subdivision"] = this.person.mailing_address.subdivision;
                                        }
                                        if (this.person.mailing_address.locality) {
                                            address["locality"] = this.person.mailing_address.locality;
                                        }
                                        if (this.person.mailing_address.postal_code) {
                                            address["postal_code"] = this.person.mailing_address.postal_code;
                                        }
                                        if (this.person.mailing_address.country) {
                                            address["country_id"] = this.person.mailing_address.country.id;
                                            address['country_title'] = this.person.mailing_address.country.title;
                                        }
                                        if (this.person.mailing_address.mobile_phone_number) {
                                            address["mobile_phone_number"] = this.person.mailing_address.mobile_phone_number;
                                        }
                                        if (this.person.mailing_address.what3words_address) {
                                            address["what3words_address"] = this.person.mailing_address.what3words_address;
                                        }
                                        if (this.person.mailing_address.courier_instructions) {
                                            address["courier_instructions"] = this.person.mailing_address.courier_instructions;
                                        }

                                        addresses.push(address)

                                    }

                                    await this.loadPersonShippingAddresses(this.person.id).then((shipping_addresses: any) => {

                                        if(shipping_addresses) {
    
                                            for(let i = 0; i < shipping_addresses.length; i++) {

                                                let address = {}

                                                address['weight'] = 300;
                                                address['type'] = "Shipping address";
                                                address['entity_type'] = 'person';
                                                address['shipping_address_id'] = shipping_addresses[i].id;
    
                                                if(shipping_addresses[i].first_name) {
                                                    address['first_name'] = shipping_addresses[i].first_name;
                                                }
                                                if (shipping_addresses[i].last_name) {
                                                    address['last_name'] = shipping_addresses[i].last_name;
                                                }
                                                if (shipping_addresses[i].organisation_title) {
                                                    address["organisation_title"] = shipping_addresses[i].organisation_title;
                                                }
                                                if (shipping_addresses[i].premises) {
                                                    address["premises"] = shipping_addresses[i].premises;
                                                }
                                                if (shipping_addresses[i].line_1) {
                                                    address["line_1"] = shipping_addresses[i].line_1;
                                                }
                                                if (shipping_addresses[i].line_2) {
                                                    address["line_2"] = shipping_addresses[i].line_2;
                                                }
                                                if (shipping_addresses[i].subdivision) {
                                                    address["subdivision"] = shipping_addresses[i].subdivision;
                                                }
                                                if (shipping_addresses[i].locality) {
                                                    address["locality"] = shipping_addresses[i].locality;
                                                }
                                                if (shipping_addresses[i].postal_code) {
                                                    address["postal_code"] = shipping_addresses[i].postal_code;
                                                }
                                                if (shipping_addresses[i].country) {
                                                    address["country_id"] = shipping_addresses[i].country.id;
                                                    address['country_title'] = shipping_addresses[i].country.title;
                                                }
                                                if (shipping_addresses[i].mobile_phone_number) {
                                                    address["mobile_phone_number"] = shipping_addresses[i].mobile_phone_number;
                                                }
                                                if (shipping_addresses[i].what3words_address) {
                                                    address["what3words_address"] = shipping_addresses[i].what3words_address;
                                                }
                                                if (shipping_addresses[i].courier_instructions) {
                                                    address["courier_instructions"] = shipping_addresses[i].courier_instructions;
                                                }
    
                                                addresses.push(address)
    
                                            }
                                        }
                                    })

                                }

                                addresses.sort((a, b) => {
                                    if (a.weight > b.weight) { return -1; }
                                    if (a.weight < b.weight) { return 1; }
                                    return 0;
                                });

                                this.addresses = addresses;

                                this.loading = false;
                                resolve();

                            }
                            else {
                                this.loading = false;
                                reject();
                            }
                        })
                        .catch(() => {
                            this.loading = false;
                            reject();
                        });
                    }
                    else {
                        this.loading = false;
                        reject();
                    }
                }
                else {
                    this.loading = false;
                    reject();
                }
            })
            .catch(() => {
                this.loading = false;
                reject();
            });
        });
    }

    async searchAddresses() {

        if(this.addresses){

            this.visible_addresses = null;

            if(this.query && this.query.length) {

                let query = this.query.toLowerCase().trim();

                let addresses = [];

                for(let i = 0; i < this.addresses.length; i++) {

                    if(this.addresses[i].first_name && this.addresses[i].first_name.toLowerCase().includes(query)) {
                        addresses.push(this.addresses[i]);
                    }
                    else if(this.addresses[i].last_name && this.addresses[i].last_name.toLowerCase().includes(query)) {
                        addresses.push(this.addresses[i]);
                    }
                    else if(this.addresses[i].organisation_title && this.addresses[i].organisation_title.toLowerCase().includes(query)) {
                        addresses.push(this.addresses[i]);
                    }
                    else if(this.addresses[i].premises && this.addresses[i].premises.toLowerCase().includes(query)) {
                        addresses.push(this.addresses[i]);
                    }
                    else if(this.addresses[i].line_1 && this.addresses[i].line_1.toLowerCase().includes(query)) {
                        addresses.push(this.addresses[i]);
                    }
                    else if(this.addresses[i].line_2 && this.addresses[i].line_2.toLowerCase().includes(query)) {
                        addresses.push(this.addresses[i]);
                    }
                    else if(this.addresses[i].subdivision && this.addresses[i].subdivision.toLowerCase().includes(query)) {
                        addresses.push(this.addresses[i]);
                    }
                    else if(this.addresses[i].locality && this.addresses[i].locality.toLowerCase().includes(query)) {
                        addresses.push(this.addresses[i]);
                    }
                    else if(this.addresses[i].postal_code && this.addresses[i].postal_code.toLowerCase().includes(query)) {
                        addresses.push(this.addresses[i]);
                    }
                    else if(this.addresses[i].country_title && this.addresses[i].country_title.toLowerCase().includes(query)) {
                        addresses.push(this.addresses[i]);
                    }

                }

                addresses.sort((a, b) => {
                    if (a.weight > b.weight) { return -1; }
                    if (a.weight < b.weight) { return 1; }
                    return 0;
                });

                this.visible_addresses = addresses;

            }
            else {
                this.showInitialVisibleAddresses().then(() => {}).catch(() => { });
            }

        }

    }

    showInitialVisibleAddresses() {
        return new Promise<void>((resolve) => {
            if(this.addresses && !this.visible_addresses) {
                this.visible_addresses = [];
            }
            let count = this.visible_addresses.length;
            for(let i = 0; i < 25; i++) {
                let position = i + count;
                if(this.addresses.length > position) {
                    this.visible_addresses.push(this.addresses[position]);
                }
                else {
                    break;
                }
            }
            resolve();
        });
    }

    showMoreVisibleAddresses(event) {
        let count = this.visible_addresses.length;
        for(let i = 0; i < 25; i++) {
            let position = i + count;
            if(this.addresses.length > position) {
                this.visible_addresses.push(this.addresses[position]);
            } 
            else {
                break;
            }
        }
        event.target.complete();
    }

    loadPerson(person_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getPerson(person_id).then((person) => {
                    resolve(person);
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert('Unknown error', 'Unfortunately something went wrong when trying to retrieve a person.');
                    } 
                    else if(error.status == 403) {
                        this.presentAlert('Insufficient permissions', "You don't have permission to view a person.");
                    } 
                    else if(error.status == 404) {
                        this.presentAlert('Unavailable', "Unfortunately we're unable to retrieve a person at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(['switch-account'], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    loadPersonShippingAddresses(person_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getPersonShippingAddresses(person_id).then((shipping_addresses) => {
                    resolve(shipping_addresses);
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert('Unknown error', 'Unfortunately something went wrong when trying to retrieve a list of shipping addresses.');
                    } 
                    else if(error.status == 403) {
                        this.presentAlert('Insufficient permissions', "You don't have permission to view shipping addresses.");
                    } 
                    else if(error.status == 404) {
                        this.presentAlert('Unavailable', "Unfortunately we're unable to retrieve a list of shipping addresses at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(['switch-account'], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    // TODO: replace with get organisation addresses for lighter API?
    loadOrganisation(organisation_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrganisation(organisation_id).then((organisation) => {
                    resolve(organisation);
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert('Unknown error', 'Unfortunately something went wrong when trying to retrieve an organisation.');
                    } 
                    else if(error.status == 403) {
                        this.presentAlert('Insufficient permissions', "You don't have permission to view an organisation.");
                    } 
                    else if(error.status == 404) {
                        this.presentAlert('Unavailable', "Unfortunately we're unable to retrieve an organisation at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(['switch-account'], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    loadOrganisationShippingAddresses(organisation_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrganisationShippingAddresses(organisation_id).then((shipping_addresses) => {
                    resolve(shipping_addresses);
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert("Unknown error", "Unfortunately something went wrong when trying to retrieve a list of organisation shipping addresses.");
                    } 
                    else if(error.status == 403) {
                        this.presentAlert("Insufficient permissions", "You don't have permission to view a list of organisation shipping addresses.");
                    } 
                    else if(error.status == 404) {
                        this.presentAlert("Unavailable", "Unfortunately we're unable to retrieve a list of organisation shipping addresses at this time.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(['switch-account'], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    close() {
        this.modalController.dismiss();
    }

    select(address_index) {

        let address = this.addresses[address_index];

        delete this.params['first_name'];
        delete this.params['last_name'];
        delete this.params['organisation_title'];
        delete this.params['premises'];
        delete this.params['line_1'];
        delete this.params['line_2'];
        delete this.params['locality'];
        delete this.params['subdivision'];
        delete this.params['postal_code'];
        delete this.params['country_id'];
        delete this.params['mobile_phone_number'];
        delete this.params['what3words_address'];
        delete this.params['courier_instructions'];

        if(address.first_name) {
            this.params['first_name'] = address.first_name;
        }
        if(address.last_name) {
            this.params['last_name'] = address.last_name;
        }
        if(address.organisation_title) {
            this.params['organisation_title'] = address.organisation_title;
        }
        if(address.premises) {
            this.params['premises'] = address.premises;
        }
        if(address.line_1) {
            this.params['line_1'] = address.line_1;
        }
        if(address.line_2) {
            this.params['line_2'] = address.line_2;
        }
        if(address.locality) {
            this.params['locality'] = address.locality;
        }
        if(address.subdivision) {
            this.params['subdivision'] = address.subdivision;
        }
        if(address.postal_code) {
            this.params['postal_code'] = address.postal_code;
        }
        if(address.country_id) {
            this.params['country_id'] = address.country_id;
        }
        if(address.mobile_phone_number) {
            this.params['mobile_phone_number'] = address.mobile_phone_number;
        }
        if(address.what3words_address) {
            this.params['what3words_address'] = address.what3words_address;
        }
        if(address.courier_instructions) {
            this.params['courier_instructions'] = address.courier_instructions;
        }

        this.modalController.dismiss(this.params);

    }

    async presentAddShippingAddressModal() {
        if(!this.add_shipping_address_modal_open) {
            this.add_shipping_address_modal_open = true;
            const modal = await this.modalController.create({
                component: ShippingAddressAddPage,
                cssClass: this.modal_css_class,
                componentProps: {
                    organisation_id: this.organisation ? this.organisation.id : null,
                    person_id: this.person ? this.person.id : null
                }
            });
            modal.onDidDismiss().then((data) => {
                this.add_shipping_address_modal_open = false;
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

    async addressOptionsPopover(ev: any, address) {
        ev.stopPropagation();
        const popover = await this.popoverController.create({
            component: AddressBookShippingAddressOptionsComponent,
            event: ev,
            animated: true,
            showBackdrop: false,
            side:"left",
            alignment:"start",
            componentProps: {
                address_picker_modal: this,
                address: address,
                organisation_id: this.organisation ? this.organisation.id : null,
                person_id: this.person ? this.person.id : null
            }
        });
        return await popover.present();
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

}
